import {
	Component,
	Input,
	Output,
	EventEmitter,
	OnInit,
	OnDestroy,
	ChangeDetectionStrategy,
	ChangeDetectorRef,
	forwardRef,
} from "@angular/core";
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from "@angular/forms";
import { Subject, takeUntil } from "rxjs";

// Models
import { TitularResiduosNoPeligrososViewModel } from "src/app/_core/models/centros/cebe-contract-list-base.model";

// Services
import { MasterService } from "src/app/_core/providers/master.service";
import { NotificationService } from "src/app/_core/providers/notification.service";

@Component({
    selector: "app-titular-residuos-no-peligrosos-autocomplete",
    templateUrl: "./titular-residuos-no-peligrosos-autocomplete.component.html",
    styleUrls: ["./titular-residuos-no-peligrosos-autocomplete.component.scss"],
    changeDetection: ChangeDetectionStrategy.OnPush,
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => TitularResiduosNoPeligrososAutocompleteComponent),
            multi: true,
        },
    ],
    standalone: false
})
export class TitularResiduosNoPeligrososAutocompleteComponent
	implements OnInit, OnDestroy, ControlValueAccessor
{
	private readonly destroy$ = new Subject<void>();

	// Inputs
	@Input() label = "Titular Residuos No Peligrosos";
	@Input() placeholder =
		"Buscar titular de residuos no peligrosos por código o descripción";
	@Input() required = false;
	@Input() disabled = false;
	@Input() hint =
		"Busque por código o descripción de titular de residuos no peligrosos";

	// Outputs
	@Output() titularResiduosNoPeligrososSelected =
		new EventEmitter<TitularResiduosNoPeligrososViewModel>();

	// State
	titularesResiduosNoPeligrosos: TitularResiduosNoPeligrososViewModel[] = [];
	titularResiduosNoPeligrososId: number | null = null;
	selectedTitularResiduosNoPeligrosos: TitularResiduosNoPeligrososViewModel | null =
		null;

	// ControlValueAccessor
	private onChange = (_value: number | null): void => {
		// This is intentional
	};
	private onTouched = (): void => {
		// This is intentional
	};

	constructor(
		private readonly masterService: MasterService,
		private readonly notificationService: NotificationService,
		private readonly cdr: ChangeDetectorRef,
	) {}

	ngOnInit(): void {
		this.loadTitularesResiduosNoPeligrosos();
	}

	ngOnDestroy(): void {
		this.destroy$.next();
		this.destroy$.complete();
	}

	// ControlValueAccessor implementation
	writeValue(value: number | null): void {
		this.titularResiduosNoPeligrososId = value ?? null;
		this.syncSelected();
		this.cdr.markForCheck();
	}

	registerOnChange(fn: (value: number | null) => void): void {
		this.onChange = fn;
	}

	registerOnTouched(fn: () => void): void {
		this.onTouched = fn;
	}

	setDisabledState(isDisabled: boolean): void {
		this.disabled = isDisabled;
		this.cdr.markForCheck();
	}

	/**
	 * Carga la lista de titulares de residuos no peligrosos
	 */
	private loadTitularesResiduosNoPeligrosos(): void {
		this.masterService
			.getTitularResiduosNoPeligrososList()
			.pipe(takeUntil(this.destroy$))
			.subscribe({
				next: (titularesResiduosNoPeligrosos) => {
					this.titularesResiduosNoPeligrosos =
						titularesResiduosNoPeligrosos;
					this.syncSelected();
					this.cdr.markForCheck();
				},
				error: () => {
					this.notificationService.showError(
						"Error al cargar los titulares de residuos no peligrosos",
					);
				},
			});
	}

	/** Recalcula el objeto seleccionado a partir del id actual. */
	private syncSelected(): void {
		this.selectedTitularResiduosNoPeligrosos =
			this.titularResiduosNoPeligrososId != null
				? (this.titularesResiduosNoPeligrosos.find(
						(t) => t.id === this.titularResiduosNoPeligrososId,
					) ?? null)
				: null;
	}

	/**
	 * Obtiene el texto mostrado para un titular de residuos no peligrosos
	 */
	displayTitularResiduosNoPeligrosos = (
		titularResiduosNoPeligrosos:
			| TitularResiduosNoPeligrososViewModel
			| string,
	): string => {
		if (typeof titularResiduosNoPeligrosos === "string") {
			return titularResiduosNoPeligrosos;
		}
		return titularResiduosNoPeligrosos &&
			titularResiduosNoPeligrosos.codigo &&
			titularResiduosNoPeligrosos.descripcion
			? `${titularResiduosNoPeligrosos.codigo} - ${titularResiduosNoPeligrosos.descripcion}`
			: "";
	};

	/** Selección desde el autocomplete `.ds`. */
	onValueChange(
		titularResiduosNoPeligrosos: TitularResiduosNoPeligrososViewModel | null,
	): void {
		this.titularResiduosNoPeligrososId = titularResiduosNoPeligrosos
			? titularResiduosNoPeligrosos.id
			: null;
		this.selectedTitularResiduosNoPeligrosos = titularResiduosNoPeligrosos;
		this.onChange(this.titularResiduosNoPeligrososId);
		this.onTouched();
		if (titularResiduosNoPeligrosos) {
			this.titularResiduosNoPeligrososSelected.emit(
				titularResiduosNoPeligrosos,
			);
		}
		this.cdr.markForCheck();
	}

	onControlTouched(): void {
		this.onTouched();
	}
}
